import { BillStatus } from '../../../__generated__/globalTypes';
import { getRelativeDateBucket, pluralize } from '../../../utils/formatting';
import Badge from '../../base/badges/Badge';

import type { RelativeDateBucket } from '../../../utils/formatting';
import type { BadgeColors } from '../../base/badges/types';
import type { CalendarDate } from '@internationalized/date';

type TimingVariant = 'bill' | 'recurring';
type TimingInfo = { label: string; color: BadgeColors };

function getBillTimingFromBucket(bucket: RelativeDateBucket): TimingInfo {
  if (bucket.direction === 'today') {
    return { label: 'Due today', color: 'warning' };
  }
  if (bucket.direction === 'past') {
    return { label: `${pluralize(bucket.value, bucket.unit)} overdue`, color: 'error' };
  }
  if (bucket.value === 1 && bucket.unit === 'day') {
    return { label: 'Due tomorrow', color: 'brand' };
  }
  return { label: `Due in ${pluralize(bucket.value, bucket.unit)}`, color: 'brand' };
}

function getRecurringTimingFromBucket(bucket: RelativeDateBucket): TimingInfo {
  if (bucket.direction === 'today') {
    return { label: 'Expected today', color: 'warning' };
  }
  if (bucket.direction === 'past') {
    return { label: 'Past expected date', color: 'error' };
  }
  const label =
    bucket.value === 1 && bucket.unit === 'day'
      ? 'Expected tomorrow'
      : `Expected in ${pluralize(bucket.value, bucket.unit)}`;
  return { label, color: 'brand' };
}

const STATUS_OVERRIDES: Partial<Record<BillStatus, TimingInfo>> = {
  [BillStatus.Paid]: { label: 'Paid', color: 'success' },
  [BillStatus.PaidElsewhere]: { label: 'Paid elsewhere', color: 'success' },
  [BillStatus.WontPay]: { label: "Won't pay", color: 'success' },
};

function getTimingInfo(date: CalendarDate, variant: TimingVariant, status?: BillStatus): TimingInfo {
  if (status) {
    const override = STATUS_OVERRIDES[status];
    if (override) {
      return override;
    }
  }
  const bucket = getRelativeDateBucket(date);
  return variant === 'bill' ? getBillTimingFromBucket(bucket) : getRecurringTimingFromBucket(bucket);
}

interface ISlideoutTimingTagProps {
  date: CalendarDate;
  /** @default 'bill' */
  variant?: TimingVariant;
  status?: BillStatus;
}

const SlideoutTimingTag = ({ date, variant = 'bill', status }: ISlideoutTimingTagProps) => {
  const { label, color } = getTimingInfo(date, variant, status);

  return (
    <Badge type="pill-color" size="lg" color={color}>
      {label}
    </Badge>
  );
};

export default SlideoutTimingTag;
